import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { docCurrencySymbol, formatDocAmount, toTenantAmount } from '@/utils/purchase-currency';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageContainer, Button, Input, Select, Skeleton, ErrorBoundary, FileUpload, useToast } from '@bbpos/ui';
import { GLASS_FORM } from '@/constants/design-tokens';
import { usePurchaseOrder, usePurchaseOrders, PlaceOfSupply, gstTaxLines, isGstSplit } from '@/features/purchase-orders';
import { computePoLine, taxRateOptions } from '@/features/purchase-orders/line-totals';
import {
  useVendorBill, useCreateVendorBill, useUpdateVendorBill, usePostVendorBill,
  uploadPurchaseDocument, DocumentUploadError, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES,
  billQtyError, billsAheadOfDelivery, unbilledOrdered,
} from '@/features/vendor-bills';
import type { VendorBill, VendorBillBasis, PurchaseDocumentPayload, CreateVendorBillPayload, UpdateVendorBillPayload, VendorBillItemPayload } from '@/features/vendor-bills';
import { productService } from '@/services/product.service';
import { ingredientService } from '@/services/ingredient.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { buildProductSearchParams } from '../purchase-orders/purchase-order-products';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '../purchase-orders/icons';
import { seedRows, newRowKey } from './vendor-bill-seed';
import type { LineKind, LineRow } from './vendor-bill-seed';

const PAYMENT_TERM_DAYS: Record<string, number> = { IMMEDIATE: 0, NET_15: 15, NET_30: 30, NET_45: 45, NET_60: 60, NET_90: 90 };
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, days: number) => { const d = new Date(iso); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
const key = newRowKey;
const blankRow = (kind: LineKind): LineRow => ({
  key: key(), kind, po_item_id: null, product_id: null, ingredient_id: null, item_name: '', unit: '', quantity: '1', unit_cost: '', gst_rate: '0', discount_type: 'FLAT', discount_value: '',
});

function toInput(row: LineRow, currency: string = 'INR') {
  const discount = parseFloat(row.discount_value) || 0;
  const subunit = getSubunitMultiplier(currency);
  return {
    quantity: parseFloat(row.quantity) || 0,
    unit_cost: Math.round((parseFloat(row.unit_cost) || 0) * subunit),
    gst_rate: parseInt(row.gst_rate, 10) || 0,
    discount_type: row.discount_type,
    // PERCENTAGE becomes basis points — ×100 in every currency. FLAT becomes
    // minor units, which is only ×100 where the currency has two decimals.
    discount_value:
      row.discount_type === 'PERCENTAGE'
        ? Math.round(discount * 100) // not-money: percent → basis points
        : Math.round(discount * subunit),
  };
}

function toItemPayload(r: LineRow, currency: string = 'INR'): VendorBillItemPayload {
  const ref = r.po_item_id ? { po_item_id: r.po_item_id }
    : r.product_id ? { product_id: r.product_id }
      : r.ingredient_id ? { ingredient_id: r.ingredient_id }
        : { item_name: r.item_name.trim(), ...(r.add_to_stock_cost ? { add_to_stock_cost: true } : {}) };
  return { ...ref, ...toInput(r, currency), unit: r.unit || undefined };
}

function VendorBillCreateContent() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const tenantCurrency = useTenantCurrency();
  const tax = useTaxRegime();
  const createMutation = useCreateVendorBill();
  const updateMutation = useUpdateVendorBill();
  const postMutation = usePostVendorBill();
  const editing = Boolean(editId);
  const { data: existing, isLoading: existingLoading } = useVendorBill(editId);

  const [poId, setPoId] = useState(searchParams.get('po_id') ?? '');
  const grnId = editing ? undefined : (searchParams.get('grn_id') ?? undefined);
  const [basis, setBasis] = useState<VendorBillBasis>((searchParams.get('basis') as VendorBillBasis) || 'RECEIVED');
  const [poSearch, setPoSearch] = useState('');
  const { data: poList } = usePurchaseOrders({ page: 1, limit: 20, search: poSearch || undefined });
  const { data: po, isLoading: poLoading } = usePurchaseOrder(poId || undefined);
  // A bill is in its PO's currency (docs/multi-currency-purchasing-plan.md D3).
  const currency = po?.currency_code ?? tenantCurrency;
  const symbol = docCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [billDate, setBillDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  // T07: the round-off printed on the supplier's bill, typed in major units (signed).
  const [roundOff, setRoundOff] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Vendor invoice(s) uploaded on Receive Goods for this receipt — the API
  // adopts them onto the bill, so the user needn't upload the file twice.
  const receiptDocs = useMemo(
    () => (grnId ? (po?.documents ?? []).filter((d) => d.goods_receipt_id === grnId && !d.vendor_bill_id) : []),
    [po, grnId],
  );
  const [rows, setRows] = useState<LineRow[]>([]);
  const [seededFor, setSeededFor] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  // Catalog picker for ad-hoc lines — same source as the New PO page: restaurant
  // tenants buy ingredients, retail tenants buy products (full active catalog, not location-scoped).
  const businessType = useAuthStore((s) => s.profile?.tenant?.business_type);
  const isRestaurant = businessType === 'RESTAURANT';
  const [productSearch, setProductSearch] = useState('');
  const { data: productsData } = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => productService.list(buildProductSearchParams(productSearch)),
    staleTime: 15_000,
    enabled: Boolean(po) && !isRestaurant,
  });
  const { data: ingredientsData } = useQuery({
    queryKey: ['ingredients-search'],
    queryFn: () => ingredientService.listIngredients({ is_active: 'true', limit: 200 }),
    staleTime: 15_000,
    enabled: Boolean(po) && isRestaurant,
  });
  const catalog = useMemo(() => {
    if (isRestaurant) return (ingredientsData?.data ?? []).map((g) => ({ id: g.id, name: g.name, label: g.name, unit: g.unit, cost: g.cost, gst_rate: 0 }));
    return (productsData?.data ?? []).map((p) => ({
      id: p.id, name: p.name, label: `${p.name} (${p.sku})`, unit: p.unit, cost: p.cost,
      gst_rate: (p as { purchase_gst_rate?: number | null }).purchase_gst_rate ?? p.gst_rate ?? 0,
    }));
  }, [isRestaurant, ingredientsData, productsData]);
  const catalogOptions = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return catalog.filter((c) => !isRestaurant || !q || c.name.toLowerCase().includes(q)).map((c) => ({ value: c.id, label: c.label }));
  }, [catalog, isRestaurant, productSearch]);

  // Edit mode: the bill fixes the PO and prefills the header.
  useEffect(() => {
    if (!existing) return;
    setPoId(existing.purchase_order_id);
    setBasis(existing.basis);
    setInvoiceNumber(existing.vendor_invoice_number ?? '');
    setInvoiceDate(existing.vendor_invoice_date?.slice(0, 10) ?? '');
    setBillDate(existing.bill_date.slice(0, 10));
    setDueDate(existing.due_date?.slice(0, 10) ?? '');
    setNotes(existing.notes ?? '');
  }, [existing]);
  useEffect(() => {
    if (!existing) return;
    const c = existing.currency_code ?? tenantCurrency;
    setRoundOff(existing.round_off ? (existing.round_off / getSubunitMultiplier(c)).toFixed(getCurrencyDecimals(c)) : '');
  }, [existing, tenantCurrency]);

  // Seed lines from the PO whenever the PO or basis changes (a receipt's own
  // quantities win for basis RECEIVED when arriving from the receive screen).
  // In edit mode the draft's lines win and a basis change keeps them.
  useEffect(() => {
    if (!po || (editing && !existing)) return;
    const seedKey = editing ? `edit:${existing!.id}` : `${po.id}:${basis}:${grnId ?? ''}`;
    if (seededFor === seedKey) return;
    const grn = grnId ? po.receiving_history.find((g) => g.id === grnId) : undefined;
    setRows(seedRows(po, basis, grnId, editing ? (existing ?? null) : null, currency));
    if (!editing && grn?.vendor_invoice_number) setInvoiceNumber(grn.vendor_invoice_number);
    if (!editing && grn?.vendor_invoice_date) setInvoiceDate(grn.vendor_invoice_date.slice(0, 10));
    setSeededFor(seedKey);
  }, [po, basis, grnId, seededFor, editing, existing]);

  // Due date from the supplier's payment terms once the PO (and its supplier) is known.
  useEffect(() => {
    if (!po || dueDate) return;
    const terms = po.supplier_payment_terms;
    setDueDate(addDays(billDate, PAYMENT_TERM_DAYS[terms ?? 'NET_30'] ?? 30));
  }, [po, billDate, dueDate]);

  const lineErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    for (const r of rows) {
      const qty = parseFloat(r.quantity) || 0;
      if (r.po_item_id && r.ordered != null) {
        const e = billQtyError({ po_item_id: r.po_item_id, item_name: r.item_name, unit: r.unit, ordered: r.ordered, received: r.received ?? 0, billed: r.billed ?? 0, unit_cost: 0, gst_rate: 0, discount_type: 'FLAT', discount_value: 0 }, qty);
        if (e) errs[r.key] = e;
      }
      if (r.kind === 'free' && qty > 0 && !r.item_name.trim()) errs[r.key] = 'Describe this line';
      if (r.kind === 'catalog' && qty > 0 && !r.product_id && !r.ingredient_id) errs[r.key] = isRestaurant ? 'Pick an ingredient' : 'Pick a product';
    }
    return errs;
  }, [rows, isRestaurant]);

  const activeRows = rows.filter((r) => (parseFloat(r.quantity) || 0) > 0);
  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, tax = 0, total = 0;
    const taxItems: { gst_rate: number; line_tax: number }[] = [];
    for (const r of activeRows) {
      const input = toInput(r, currency);
      const t = computePoLine(input, tenantCurrency);
      subtotal += t.line_subtotal; discount += t.line_discount; tax += t.line_tax; total += t.line_total;
      taxItems.push({ gst_rate: input.gst_rate, line_tax: t.line_tax });
    }
    // CGST + SGST or IGST by the PO's supply type (QA PU1-004).
    const taxLines = isGstSplit(po?.supply) ? gstTaxLines(po.supply, taxItems) : [];
    return { subtotal, discount, tax, total, taxLines };
  }, [activeRows]);
  const roundOffMinor = Math.round((parseFloat(roundOff) || 0) * subunit);
  const roundOffError = Math.abs(roundOffMinor) >= subunit ? `Must be less than 1 ${currency}` : '';
  const billTotal = totals.total + roundOffMinor;

  function update(k: string, field: keyof LineRow, value: string) {
    setRows((prev) => prev.map((r) => (r.key === k ? { ...r, [field]: value } : r)));
  }
  function pickCatalog(k: string, catalogId: string) {
    const c = catalog.find((x) => x.id === catalogId);
    setRows((prev) => prev.map((r) => (r.key !== k ? r : {
      ...r,
      product_id: isRestaurant ? null : catalogId || null, ingredient_id: isRestaurant ? catalogId || null : null,
      item_name: c?.name ?? r.item_name, unit: c?.unit ?? r.unit,
      // A catalogue cost is in the tenant currency: it prices a tenant-currency bill only.
      unit_cost: c && !po?.currency_code ? (c.cost / subunit).toFixed(dp) : r.unit_cost, gst_rate: c ? String(c.gst_rate) : r.gst_rate,
    })));
  }

  const canSubmit = Boolean(po) && activeRows.length > 0 && Object.keys(lineErrors).length === 0 && !roundOffError && (!editing || existing?.status === 'DRAFT');

  async function handleSubmit(postAfterSave: boolean) {
    if (!po || !canSubmit) return;
    let document: PurchaseDocumentPayload | undefined;
    if (file) {
      setUploading(true);
      try {
        document = await uploadPurchaseDocument(file);
      } catch (err) {
        setUploading(false);
        toast({ title: err instanceof DocumentUploadError ? err.message : 'Invoice upload failed', variant: 'error' });
        return; // never save a bill claiming an attachment it doesn't have
      }
      setUploading(false);
    }
    const body: UpdateVendorBillPayload = {
      basis,
      vendor_invoice_number: invoiceNumber.trim() || undefined,
      vendor_invoice_date: invoiceDate || undefined,
      bill_date: billDate || undefined,
      due_date: dueDate || undefined,
      notes: notes.trim() || undefined,
      round_off: roundOffMinor,
      document,
      // Explicit arrow: point-free `.map(toItemPayload)` would hand the array
      // index in as the currency now that the helper takes one.
      items: activeRows.map((r) => toItemPayload(r, currency)),
    };
    const onSaved = (bill: VendorBill) => {
      // The same supplier invoice on another bill: keep it a draft and say which bill (QA PU4-004).
      if (bill.duplicate_of) {
        toast({ title: `${bill.bill_number} saved as draft — vendor invoice ${bill.vendor_invoice_number} is already on ${bill.duplicate_of.bill_number}. Check it before posting.`, variant: 'warning' });
        navigate(`/vendor-bills/${bill.id}`);
        return;
      }
      if (!postAfterSave) {
        toast({ title: `${bill.bill_number} saved as draft — ${formatDocAmount(bill.total_amount, currency)}`, variant: 'success' });
        navigate(`/vendor-bills/${bill.id}`);
        return;
      }
      setPosting(true);
      postMutation.mutate({ id: bill.id }, {
        onSuccess: (posted) => { toast({ title: `${posted.bill_number} posted — ${formatDocAmount(posted.total_amount, currency)} due ${posted.due_date ? new Date(posted.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}`, variant: 'success' }); navigate(`/vendor-bills/${posted.id}`); },
        onError: (err: Error) => { toast({ title: `Saved as draft, but posting failed: ${err.message}`, variant: 'error' }); navigate(`/vendor-bills/${bill.id}`); },
        onSettled: () => setPosting(false),
      });
    };
    const onError = (err: Error) => toast({ title: err.message, variant: 'error' });
    if (editing && existing) {
      updateMutation.mutate({ id: existing.id, payload: body }, { onSuccess: onSaved, onError });
    } else {
      const payload: CreateVendorBillPayload = { ...body, purchase_order_id: po.id, goods_receipt_id: basis === 'RECEIVED' ? grnId : undefined };
      createMutation.mutate(payload, { onSuccess: onSaved, onError });
    }
  }

  const saving = uploading || createMutation.isPending || updateMutation.isPending || posting;
  const savingLabel = uploading ? 'Uploading invoice…' : posting ? 'Posting…' : 'Saving…';
  const poLocked = editing || Boolean(searchParams.get('po_id'));

  if (editing && existingLoading) return <Skeleton variant="rect" height={320} className="rounded-xl" />;
  if (editing && existing && existing.status !== 'DRAFT') {
    return (
      <div className={`${GLASS_FORM} p-6 animate-fade-in`}>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">{existing.bill_number} is {existing.status.toLowerCase().replace('_', ' ')} — only drafts can be edited.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(`/vendor-bills/${existing.id}`)}><ArrowLeftIcon className="h-4 w-4 mr-1.5" />Back to bill</Button>
      </div>
    );
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-warning-500 via-orange-500 to-danger-500 p-6 text-white shadow-[0_24px_60px_-24px_rgba(234,88,12,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{editing ? `Edit ${existing?.bill_number ?? 'Vendor Bill'}` : 'New Vendor Bill'}</h1>
            <p className="text-orange-100 mt-1">{editing ? 'Adjust the draft, then save it or post it to make it payable' : 'Record what the supplier is charging you — for the delivered goods, or the full order to lock the price'}</p>
          </div>
          {editing && <span className="ml-auto rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">Draft</span>}
        </div>
      </div>

      <div className={`${GLASS_FORM} p-6 animate-slide-up space-y-6`}>
        {/* PO + basis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Purchase order <span className="text-danger-500">*</span></label>
            {poLocked && po ? (
              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-2 text-sm">
                <span className="font-mono font-semibold">{po.po_number}</span> · {po.supplier_name} · {po.location_name}
              </div>
            ) : (
              <Select
                variant="searchable"
                options={(poList?.data ?? []).filter((p) => ['ORDERED', 'PARTIALLY_RECEIVED', 'RECEIVED'].includes(p.status)).map((p) => ({ value: p.id, label: `${p.po_number} · ${p.supplier_name} · ${formatDocAmount(p.total_amount, p.currency_code ?? tenantCurrency)}` }))}
                value={poId}
                onChange={(v) => { setPoId(typeof v === 'string' ? v : ''); setSeededFor(''); setDueDate(''); }}
                placeholder="Search PO number or supplier"
              />
            )}
            {!poLocked && <Input className="mt-2" placeholder="Filter POs…" value={poSearch} onChange={setPoSearch} />}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Bill for</label>
            <div role="radiogroup" className="grid grid-cols-2 gap-2">
              {([
                { value: 'RECEIVED', label: 'Delivered goods', hint: grnId ? 'This receipt only' : 'Everything received, not yet billed' },
                { value: 'ORDERED', label: 'Full order', hint: 'Everything ordered — locks the price before delivery' },
              ] as const).map((opt) => (
                <button key={opt.value} type="button" role="radio" aria-checked={basis === opt.value} onClick={() => { setBasis(opt.value); if (!editing) setSeededFor(''); }}
                  className={`rounded-lg border p-3 text-left transition-colors duration-200 ${basis === opt.value ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'}`}>
                  <div className="text-sm font-semibold text-neutral-900 dark:text-white">{opt.label}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {poLoading && <Skeleton variant="rect" height={160} className="rounded-xl" />}

        {po && (
          <>
            {/* Invoice + dates */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Vendor invoice #</label><Input value={invoiceNumber} onChange={setInvoiceNumber} placeholder="INV-77" /></div>
              <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Invoice date</label><Input type="date" value={invoiceDate} onChange={setInvoiceDate} /></div>
              <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Bill date</label><Input type="date" value={billDate} onChange={(v) => { setBillDate(v); setDueDate(''); }} /></div>
              <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Due date</label><Input type="date" value={dueDate} onChange={setDueDate} /></div>
            </div>

            {/* Lines */}
            <PlaceOfSupply supply={po?.supply} />
            <div className="overflow-visible">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Item</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-28">Qty</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Unit cost ({symbol})</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-24">{tax.taxName} %</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-36">Discount</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Line total</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const qty = parseFloat(r.quantity) || 0;
                    const t = computePoLine(toInput(r, currency), tenantCurrency);
                    const st = r.po_item_id && r.ordered != null ? { po_item_id: r.po_item_id, item_name: r.item_name, unit: r.unit, ordered: r.ordered, received: r.received ?? 0, billed: r.billed ?? 0, unit_cost: 0, gst_rate: 0, discount_type: 'FLAT' as const, discount_value: 0 } : null;
                    const ahead = st ? qty > 0 && billsAheadOfDelivery(st, qty) : false;
                    // Both sides after discount: a receipt cost is already net of the PO discount.
                    const variance = r.po_net_unit_cost != null && qty > 0
                      ? Math.round((t.line_subtotal - t.line_discount) / qty) - r.po_net_unit_cost
                      : 0;
                    const catalogId = r.product_id ?? r.ingredient_id ?? '';
                    return (
                      <tr key={r.key} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0 align-top">
                        <td className="py-2 pr-3">
                          {r.kind === 'po' ? (
                            <>
                              <div className="font-medium text-neutral-800 dark:text-neutral-200">{r.item_name}</div>
                              <div className="text-[11px] text-neutral-500 tabular-nums">
                                Ordered {r.ordered} · Received {r.received} · Billed {r.billed} · {st ? unbilledOrdered(st) : 0} {r.unit} left
                              </div>
                              {ahead && <div className="text-[11px] text-warning-700 dark:text-warning-300">Billing ahead of delivery</div>}
                              {r.cost_source && <div className="text-[11px] text-neutral-500 dark:text-neutral-400">{r.cost_source}</div>}
                              {variance !== 0 && qty > 0 && <div className={`text-[11px] ${variance > 0 ? 'text-danger-600' : 'text-success-600'}`}>{variance > 0 ? '+' : '−'}{formatDocAmount(Math.abs(variance), currency)} vs PO cost</div>}
                            </>
                          ) : r.kind === 'catalog' ? (
                            <Select
                              variant="searchable"
                              options={[
                                ...(catalogId && !catalogOptions.some((o) => o.value === catalogId) ? [{ value: catalogId, label: r.item_name || catalogId }] : []),
                                ...catalogOptions,
                              ]}
                              value={catalogId}
                              onChange={(v) => pickCatalog(r.key, typeof v === 'string' ? v : '')}
                              placeholder={isRestaurant ? 'Select ingredient' : 'Select product'}
                            />
                          ) : (
                            <>
                              <Input value={r.item_name} onChange={(v) => update(r.key, 'item_name', v)} placeholder="e.g. Freight / loading" />
                              {/* QA PU4-003: landed cost — split by value over the PO's goods on post. */}
                              <label className="mt-1 flex items-center gap-1.5 text-[11px] text-neutral-600 dark:text-neutral-300 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={r.add_to_stock_cost === true}
                                  onChange={(e) => setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, add_to_stock_cost: e.target.checked } : x)))}
                                  className="h-3.5 w-3.5 rounded border-neutral-300 text-orange-600 focus:ring-orange-500"
                                />
                                Add to stock cost
                              </label>
                            </>
                          )}
                          {r.kind !== 'po' && r.unit && <div className="text-[11px] text-neutral-500 mt-0.5">Unit: {r.unit}</div>}
                          {lineErrors[r.key] && <p className="text-xs text-danger-600 mt-0.5">{lineErrors[r.key]}</p>}
                        </td>
                        <td className="py-2 pr-3"><Input type="number" value={r.quantity} onChange={(v) => update(r.key, 'quantity', v)} placeholder="0" /></td>
                        <td className="py-2 pr-3"><Input type="number" value={r.unit_cost} onChange={(v) => update(r.key, 'unit_cost', v)} placeholder="0.00" /></td>
                        <td className="py-2 pr-3"><Select options={taxRateOptions(tenantCurrency, r.gst_rate)} value={r.gst_rate} onChange={(v) => update(r.key, 'gst_rate', typeof v === 'string' ? v : '0')} /></td>
                        <td className="py-2 pr-3">
                          <div className="flex items-stretch gap-1">
                            <div className="flex rounded-lg border border-neutral-300 dark:border-neutral-600 overflow-hidden shrink-0">
                              {(['PERCENTAGE', 'FLAT'] as const).map((dt) => (
                                <button key={dt} type="button" onClick={() => update(r.key, 'discount_type', dt)} className={`px-2 text-xs font-semibold ${r.discount_type === dt ? 'bg-orange-600 text-white' : 'bg-white dark:bg-neutral-800 text-neutral-500'}`}>{dt === 'PERCENTAGE' ? '%' : symbol}</button>
                              ))}
                            </div>
                            <Input type="number" value={r.discount_value} onChange={(v) => update(r.key, 'discount_value', v)} placeholder="0" className="flex-1 min-w-0" />
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{qty > 0 ? formatDocAmount(t.line_total, currency) : '—'}</td>
                        <td className="py-2">
                          {r.kind !== 'po' && (
                            <button type="button" onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))} className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600" title="Remove"><TrashIcon className="h-4 w-4" /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => setRows((prev) => [...prev, blankRow('catalog')])}>
                <PlusIcon className="h-4 w-4 mr-1.5" />Add {isRestaurant ? 'ingredient' : 'product'} not on the PO
              </Button>
              <Button variant="secondary" onClick={() => setRows((prev) => [...prev, blankRow('free')])}>
                <PlusIcon className="h-4 w-4 mr-1.5" />Add free-text charge (freight, packing…)
              </Button>
              {rows.some((r) => r.kind === 'catalog') && (
                <div className="w-56 ml-auto"><Input placeholder={isRestaurant ? 'Search ingredients…' : 'Search products…'} value={productSearch} onChange={setProductSearch} /></div>
              )}
            </div>

            {/* Attachment + notes + totals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Vendor invoice file</label>
                  <FileUpload variant="button" accept={DOCUMENT_ACCEPT} maxSize={DOCUMENT_MAX_BYTES} preview={file?.name ?? null} onUpload={(files: File[]) => setFile(files[0] ?? null)} />
                  {file && <p className="text-xs text-success-700 dark:text-success-300 mt-1">{file.name} selected — it will be uploaded when you save the bill.</p>}
                  {editing && (existing?.documents.length ?? 0) > 0 && (
                    <p className="text-xs text-neutral-500 mt-1">{existing!.documents.length} file{existing!.documents.length === 1 ? '' : 's'} already attached — a new upload is added alongside.</p>
                  )}
                  {receiptDocs.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {receiptDocs.map((d) => (
                        <li key={d.id} className="flex items-center justify-between gap-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 px-3 py-2 text-xs">
                          <span className="min-w-0 truncate text-orange-800 dark:text-orange-200">
                            <span className="font-medium">{d.file_name ?? d.file_key.split('/').pop()}</span>
                            {' '}· uploaded with {d.grn_number ?? 'the receipt'} — will be attached to this bill
                          </span>
                          <a href={d.url} target="_blank" rel="noreferrer" className="shrink-0 font-medium text-teal-700 dark:text-teal-300 hover:underline">View</a>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-neutral-500 mt-1">PDF or image, up to 10 MB. Kept with the bill for your records.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
                  <Input value={notes} onChange={setNotes} placeholder="Payment terms, disputes…" />
                </div>
              </div>
              <dl className="space-y-1.5 text-sm self-end">
                <div className="flex justify-between"><dt className="text-neutral-500">Subtotal</dt><dd className="tabular-nums">{formatDocAmount(totals.subtotal, currency)}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">Discount</dt><dd className="tabular-nums text-success-600">{totals.discount > 0 ? `−${formatDocAmount(totals.discount, currency)}` : '—'}</dd></div>
                {totals.taxLines.length > 0
                  ? totals.taxLines.map((l) => (
                    <div key={`${l.kind}-${l.ratePct}`} className="flex justify-between"><dt className="text-neutral-500">{l.kind} {l.ratePct}%</dt><dd className="tabular-nums">{formatDocAmount(l.amount, currency)}</dd></div>
                  ))
                  : <div className="flex justify-between"><dt className="text-neutral-500">Tax</dt><dd className="tabular-nums">{totals.tax > 0 ? formatDocAmount(totals.tax, currency) : '—'}</dd></div>}
                {/* T07: match the supplier's bill — e.g. -0.040 when it rounds down. */}
                <div className="flex justify-between items-center gap-3">
                  <dt><label htmlFor="vb-round-off" className="text-neutral-500">Round off</label></dt>
                  <dd className="w-32">
                    <Input id="vb-round-off" value={roundOff} onChange={setRoundOff} placeholder={(0).toFixed(dp)} className="text-right tabular-nums" error={roundOffError || undefined} />
                  </dd>
                </div>
                <div className="flex justify-between items-baseline pt-2 border-t border-neutral-200 dark:border-neutral-700"><dt className="text-neutral-500">Bill total</dt><dd className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">{formatDocAmount(billTotal, currency)}</dd></div>
                {po?.currency_code && po.exchange_rate && (() => {
                  const payable = toTenantAmount(billTotal, currency, tenantCurrency, String(Number(po.exchange_rate)));
                  return payable != null ? (
                    <div className="flex justify-between text-xs text-neutral-500"><dt>Payable in {tenantCurrency}</dt><dd className="tabular-nums">≈ {formatDocAmount(payable, tenantCurrency)}</dd></div>
                  ) : null;
                })()}
              </dl>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <Button variant="secondary" onClick={() => navigate(editing && existing ? `/vendor-bills/${existing.id}` : poId ? `/inventory/purchase-orders/${poId}` : '/vendor-bills')}><ArrowLeftIcon className="h-4 w-4 mr-1.5" />Cancel</Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => { void handleSubmit(false); }} disabled={saving || !canSubmit}>
              {saving && !posting ? savingLabel : 'Save draft'}
            </Button>
            <Button variant="primary" onClick={() => { void handleSubmit(true); }} disabled={saving || !canSubmit}>
              {saving ? savingLabel : 'Save & post'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export function VendorBillCreatePage() {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  return (
    <PageContainer title={editing ? 'Edit Vendor Bill' : 'New Vendor Bill'} breadcrumbs={[{ label: 'Finance' }, { label: 'Vendor Bills', href: '/vendor-bills' }, editing ? { label: 'Edit draft' } : { label: 'New' }]}>
      <ErrorBoundary><VendorBillCreateContent /></ErrorBoundary>
    </PageContainer>
  );
}
